import axios from 'axios'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import router from 'next/router'
import React from 'react'
import { useTranslation } from 'next-i18next'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import * as Sentry from '@sentry/browser'
import config from '../../../config'
import { Layout } from '../../common/Layout/Layout'
import { Meta } from '../../common/Meta'
import EventInfo from '../../components/EventInfo'
import { Header, HeaderButtons } from '../../components/Header/Header'
import LiveContent from '../../components/Live'
import BackgroundVideo from '../../components/Lobby/components/BackgroundVideo'
import SVGArrow from '../../components/Svg/ArrowLeft'
import { mediaMax } from '../../constants/breakpoints'
import { LISAEventResolved } from '../../types/lisa'
import { PageError } from '../../types/pageError'
import {
  fetchEventsForPage, mapLISAEventToViewModel,
} from '../../utils/cms/events'
import { useEvents } from '../../utils/hooks/useEvents'
import { EnumUseFavoritesError, EnumUseFavoritesSuccess, useFavorites } from '../../utils/hooks/useFavorites'
import { useLiveEvents } from '../../utils/hooks/useLiveEvents'
import { checkAuth } from '../../utils/auth'

export const getServerSideProps: GetServerSideProps = async (context) => {
  const auth = await checkAuth(context.req.headers.cookie)

  // if (!auth.user && config.LOGIN_TYPE !== 'NONE') {
  //   return {
  //     redirect: {
  //       destination: '/',
  //       permanent: false,
  //     },
  //   }
  // }
  
  const locale = (context.locale ? context.locale : context.defaultLocale) as string

  // Fetch Lisa data
  let events: LISAEventResolved[] | null = null
  try {
    [events] = await Promise.all([fetchEventsForPage()])
  }
  catch (error) {
    console.error(error)
    let pageError: PageError
    if (axios.isAxiosError(error)) {
      const { response } = error
      pageError = { error: { status: response ? response.status : 500, statusText: 'Something went wrong' } }
    }
    else {
      pageError = { error: { statusText: 'Something went wrong' } }
    }

    Sentry.setContext('events', { 'events' : events })
    Sentry.setContext('user', { 'user' : auth.user })
      
    Sentry.captureException(new Error('Error in server side props event/[slug].tsx'))

    return {
      props: {
        pageError,
      },
    }
  }

  const slug = !!context.params?.slug ? context.params.slug.toString() : ''
  const event: LISAEventResolved = events.filter((document) => document.slug === slug)[0]

  if (!event) {
    const pageError: PageError = { error: { statusText: 'Event not found' } }

    return {
      props: {
        pageError,
      },
    }
  }

  return {
    props: {
      event,
      events,
      isAuthenticated: !!auth.user,
      user: auth.user,
      ...await serverSideTranslations(locale, ['common']),
    },
  }
}

// export const getStaticPaths:GetStaticPaths<{ slug: string }> = async () => {
//   let events: LISAEventResolved[] = []
//   try {
//     [events] = await Promise.all([fetchEventsForPage()])
//   }
//   catch (error) {
//     console.log('error get static paths - Event page', error)
//   }

//   const pathsEN = events.map((event) => ({ params: { slug: event.slug }, locale: Locale.en }))
//   const pathsIS = events.map((event) => ({ params: { slug: event.slug }, locale: Locale.is }))
//   const paths = [...pathsEN, ...pathsIS]

//   return {
//     paths: paths,
//     fallback: false,
//   }
// }

interface SingleEventProps {
  event : LISAEventResolved
  events: LISAEventResolved[]
  isAuthenticated: boolean
}

const SingleEvent: React.FC<SingleEventProps> = ({ 
  event: eventProp, 
  events: eventsProp,
}: SingleEventProps) => {
  const { t } = useTranslation('common')

  const events = useEvents(eventsProp)
  const event = mapLISAEventToViewModel(eventProp)
  const liveEvents = useLiveEvents(events)

  const backUrl = eventProp.location !== undefined ? `${config.BASIC_ROUTES.program}/${eventProp.location.slugurl}` : config.BASIC_ROUTES.program

  const pageButtons: HeaderButtons = [
    { key: 1, variant: 'menu' },
    { key: 2, variant: 'home' },
    // { key: 3, variant: 'contact' },
    {
      key: 4,
      variant: 'custom',
      text: `${t('backToProgram')}`,
      icon: <SVGArrow width="53.3334%" />,
      placement: 'left',
      href: backUrl,
      id: 'menu-btn-back',
    },
  ]

  const handleFavoritesSuccess = (type: EnumUseFavoritesSuccess) => {
    toast.dismiss()
    if(type === EnumUseFavoritesSuccess.Add) {
      toast.success(
        <div>
          {t('AddedToFavorites')}
          <br />
          {t('SeeMore')}
        </div>, {
          onClick: () => {
            router.push('/favorites')
            toast.dismiss()
          },
        })
    }
    else if (type === EnumUseFavoritesSuccess.Remove) {
      toast.success(
        <div>
          {t('RemovedFromFavorites')}
          <br />
          {t('SeeMore')}
        </div>, {
          onClick: () => {
            router.push('/favorites')
            toast.dismiss()
          },
        })
    }
  }

  const handleFavoritesError = (type: EnumUseFavoritesError) => {
    if (type === EnumUseFavoritesError.Add) {
      toast.success(t('ErrAdding'))
    }

    else if (type === EnumUseFavoritesError.Remove) {
      toast.success(t('ErrRemoving'))
    }

    else if (type === EnumUseFavoritesError.InitialFetch) {
      toast.error(t('LoadingError'))
    }
  }


  const {
    favorites,
    onFavorites,
    loading: loadingFavorites,
  } = useFavorites({
    onSuccess: handleFavoritesSuccess,
    onError: handleFavoritesError,
  })

  const backgroundSrc = { src: '/Eldgos-grafik.mp4', type: 'video/mp4' }

  return (
    <Layout>
      <Head>
        <title>{`${config.PROJECT_NAME_SEO} | ${event.titleSimple}`}</title>
      </Head>
      <Meta />
      
      <Header buttons={pageButtons} liveEvents={liveEvents} />
      <Container>

        <BackgroundVideo source={backgroundSrc} loop={true} />
        
        <StyledOverlay />
        {eventProp.linkAfterLive && eventProp.linkAfterLive !== ''  && (<ContainerLiveVideo id="videocontainer">
          <LiveContent srcVideo={eventProp.linkAfterLive} />
        </ContainerLiveVideo>)}
          
        <SubContent>
          <EventInfo
            style={{ paddingTop: eventProp.linkAfterLive !== '' ? undefined : 'max(150px, 9vw)' }}
            event={event}
            type={event.linkAfterLive ? 'preview' : 'fullpage'}
            onFavorites={onFavorites}
            favoriteEvents={favorites}
            favoritesLoading={loadingFavorites}
          />
          {/* <Wrapper>
          <Container>
            <StyledOverlayTop />
            {isAuthenticated ? (
              <>
                {eventProp.linkAfterLive && eventProp.linkAfterLive !== '' && (
                  <ContainerLiveVideo id="containerlivevideo">
                    <StyledLiveContent
                      srcVideo={eventProp.linkAfterLive}
                    />
                  </ContainerLiveVideo>
                )}
              

                <EventInfo
                  style={{ paddingTop: eventProp.linkAfterLive !== '' ? undefined : 'max(150px, 9vw)' }}
                  event={event}
                  type="fullpage"
                  onFavorites={onFavorites}
                  favoriteEvents={favorites}
                  favoritesLoading={loadingFavorites}
                />
              </>
            ) : (
              <FullscreenLoading />
            )}
          </Container>
        </Wrapper> */}
        </SubContent>
      </Container>
    </Layout>
  )
}

export default SingleEvent

const Container = styled.div`
  height: 100vh;
  width: 100%;
  overflow: inherit;
  scroll-behavior: smooth;

  @media ${mediaMax.tablet} {
    flex-direction: column;
  }
`

const SubContent = styled.div`
  min-height: 100vh;
  padding-top: max(3.4vw, 50px);
  padding-bottom: 250px;
  position: relative;
  margin-bottom: 100px;
  @media ${mediaMax.mobileL} {
    padding-top: 0px;
  }
`

const StyledOverlay = styled.div`
  height: 100vh;
  width: 100%;
  position: fixed;
  z-index: -1;
  background-color: rgba(0, 0, 0, 0.4);
`


const SeparatorContainer = styled.div`
  @media ${mediaMax.tabletL} {
    display: none;
  }
`

const Wrapper = styled.div`
  height: 100vh;
  width: 100%;
  overflow: inherit;
  scroll-behavior: smooth;
`



const StyledLiveContent = styled(LiveContent)`
  padding: 18px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 1%;

  .container-iframe-video {
    margin-right: 0;
    margin-top: 0;
  }
`

const ContainerLiveVideo = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding-top: calc((50vh) - (((65vw - 8px) * 0.5625) / 2));

  @media ${mediaMax.mobileL}{
    padding: 100px 15px 30px 15px;
  }
`

const BackgroundImage = styled.div`
  height: 100vh;
  width: 100%;
  position: fixed;
  left: 0;
  top: 0;
  background-image: url(${config.BACKGROUND_image_event});
  background-size: cover;
  background-repeat: no-repeat;
  z-index: -1;
`

const StyledOverlayBottom = styled.div`
  position: absolute;
  top: 100vh;
  bottom: 0px;
  width: 100%;
  background: #121212;
`

const StyledOverlayTop = styled.div`
  height: 100vh;
  width: 100%;
  top: 0;
  position: fixed;
  background: linear-gradient(0deg, #121212 0%, rgba(18, 18, 18, 0) 100%);
`
